"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  stepNumber: "01",
  title: "Bedford SEO Audit",
  shortTitle: "Technical & Competitor Audit",
  icon: Search,
  description: "I carry out a deep technical and competitive audit of your website's current visibility across Bedford search results, identifying indexation blockers, keyword opportunities, and quick ranking wins.",
  deliverable: "Custom technical audit & prioritised action plan"
}, {
  stepNumber: "02",
  title: "Strategy and Implementation",
  shortTitle: "Tailored Local Roadmap",
  icon: FileText,
  description: "I build and execute a tailored local SEO roadmap for your business, completing hands-on on-page optimisation, structural fixes, and content updates designed to capture local search demand.",
  deliverable: "Executed on-page, technical & content roadmap"
}, {
  stepNumber: "03",
  title: "Local Search Optimisation",
  shortTitle: "GBP & Citation Signals",
  icon: MapPin,
  description: "I optimise your Google Business Profile, standardise key local business citations, and implement a targeted local content and review strategy specifically tailored to the Bedford area.",
  deliverable: "Google Business Profile & citation alignment"
}, {
  stepNumber: "04",
  title: "Monitoring and Growth",
  shortTitle: "Rankings & Enquiry Tracking",
  icon: BarChart3,
  description: "I continuously track your local keyword rankings, Google Map Pack visibility, website traffic, and incoming customer enquiries, sharing transparent monthly reports without agency fluff.",
  deliverable: "Monthly performance reports & direct advisor access"
}];
export default function Process() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-card", {
        y: 35
      });
      gsap.to(".process-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="optimizationprocess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
            Clear Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            My Bedford Local SEO Process
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            No agency runarounds or generic templates. Every stage is planned and executed directly by me, following a structured local roadmap focused on sustainable search growth.
          </p>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.stepNumber} data-index={i} className="process-card flex" style={{
              opacity: 0
            }}>
                  <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200 rounded-xl overflow-hidden relative group">
                    <div className="h-1.5 w-full bg-secondary group-hover:bg-primary transition-colors duration-200" />

                    <CardHeader className="pt-6 pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <span className="inline-flex items-center justify-center size-12 rounded-lg bg-primary text-primary-foreground font-bold text-lg font-default shadow-xs">
                          {step.stepNumber}
                        </span>

                        <div className="p-3 rounded-lg bg-secondary/60 text-foreground group-hover:text-primary transition-colors duration-200">
                          <IconComponent className="size-6" />
                        </div>
                      </div>

                      <CardTitle className="text-lg font-semibold text-foreground font-default group-hover:text-primary transition-colors duration-200">
                        {step.title}
                      </CardTitle>
                      <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-1">
                        {step.shortTitle}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex-1 flex flex-col justify-between pt-0 pb-6">
                      <p className="text-muted-foreground text-sm leading-relaxed mb-6 font-default">
                        {step.description}
                      </p>

                      <div className="pt-4 border-t border-border/60 flex items-start gap-2 text-xs text-foreground font-medium">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{step.deliverable}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 md:mt-20 max-w-4xl mx-auto rounded-2xl bg-secondary/30 border border-border p-6 md:p-8 text-center flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="text-left max-w-xl">
            <h3 className="text-xl font-semibold text-foreground font-default mb-2">
              You Work Directly With Yassine
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed font-default">
              Every audit, optimization, and reporting call is handled personally by me — ensuring direct access, complete accountability, and clear implementation.
            </p>
          </div>
          <div className="shrink-0 flex gap-3 flex-wrap">
            <Button asChild size="lg" className="rounded-md font-medium">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}